import {SupportArticleSlug} from '@frontend/modules/public/support/SupportArticleSlug'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import type {RegionCode} from './types/Locales'

// Ссылки на страницы сервиса (стандартный набор).
const commonAppUrls = {
    index: '/',
    about: '/about',
    notImplemented: '/404',
    login: '/login',
    logout: '/logout',
    registration: '/register',
    uiDemo: '/ui-demo',
    auth: {
        login: '/login',
        logout: '/logout',
        registration: '/register',
    },
    quickAdvance: {
        landing: '/quick-advance',
        survey: '/quick-advance/survey',
        quickAdCreation: '/quick-advance/item',
        requestAdvanceForAd: '/quick-advance/item/:id/request-advance',
    },
    cabinet: {
        root: '/cabinet',
        default: '/cabinet/items',
        reviews: '/cabinet/reviews',
        favorites: '/cabinet/favorites',
        subscriptions: '/cabinet/subscriptions',
        ads: {
            index: '/cabinet/items',
            create: '/cabinet/item/add',
            edit: '/cabinet/item/:id/edit',
            import: '/cabinet/items/import',
            importExampleFile: '/static/items/en/ads_import_example.xlsx',
            export: '/cabinet/items/export.xlsx',
        },
        executors: {
            index: '/cabinet/services',
            create: '/cabinet/service/add',
            edit: '/cabinet/service/:id/edit',
        },
        notifications: {
            index: '/cabinet/notifications',
        },
        chats: {
            index: '/cabinet/chats',
            messages: '/cabinet/chats',
        },
        settings: {
            index: '/cabinet/settings',
        },
        advancePayments: {
            contract: '/cabinet/advance-payments/contract',
            contractVerification: '/cabinet/advance-payments/contract/verification',
            contractRework: '/cabinet/advance-payments/contract/rework',
            contractBankAccount: '/cabinet/advance-payments/contract/bank-account',
            surveyForm: '/cabinet/advance-payments/survey',
            selectAdForAdvance: '/cabinet/advance-payments/request/item',
            requestAdvanceForAd: '/cabinet/advance-payments/request/:id',
            priceList: '/cabinet/price-list',
        },
        supportTickets: {
            index: '/cabinet/support/tickets',
            details: '/cabinet/support/tickets/:id/:uuid',
        },
        transactions: {
            index: '/cabinet/transactions',
            orderDetails: '/cabinet/orders/:orderNumber',
        },
    },
    ads: {
        all: '/listing',
        category: '/listing/:id/:slug',
        details: '/listing/:id',
    },
    services: {
        category: '/services/:id/:slug',
        details: '/services/:id',
        ownerDetails: '/service-provider/:id',
    },
    seller: {
        details: '/seller/:id',
        reviews: '/seller/:id/reviews',
    },
    serviceRequester: {
        details: '/service-requester/:id',
    },
    news: {
        list: '/news',
        details: '/news/:id/:slug',
    },
    support: {
        index: '/support',
        category: '/support/sections/:id',
        article: '/support/articles/:id',
        askQuestion: '/support/ask-question',
        rulesAndDocuments: '/rules-and-documents',
        rulesAndDocumentsArticle: '/rules-and-documents/:id',
        privacyPolicy: '/rules-and-documents/' + SupportArticleSlug.PrivacyPolicy,
        termsOfService: '/rules-and-documents/' + SupportArticleSlug.TermsOfService,
        buySellProcess: '/rules-and-documents/' + SupportArticleSlug.BuySellProcess,
        licenseAgreement: '/rules-and-documents/' + SupportArticleSlug.RulesOfOperation,
        disputeResolution: '/rules-and-documents/' + SupportArticleSlug.DisputeResolution,
    },
}

// Ссылки на страницы сервиса (тип).
type AppUrlType = typeof commonAppUrls

// Ссылки на страницы сервиса для VN региона.
const vnAppUrls: AppUrlType = {
    ...commonAppUrls,
    about: '/ve-dotoku',
    quickAdvance: {
        landing: '/dang-ky-ky-gui',
        survey: '/dang-ky-ky-gui/khao-sat',
        quickAdCreation: '/dang-ky-ky-gui/tin-dang',
        requestAdvanceForAd: '/dang-ky-ky-gui/tin-dang/:id/yeu-cau',
    },
    cabinet: {
        ...commonAppUrls.cabinet,
        ads: {
            ...commonAppUrls.cabinet.ads,
            importExampleFile: '/static/items/vn/ads_import_example.xlsx',
        },
    },
    ads: {
        all: '/tin-dang',
        category: '/tin-dang/:id/:slug',
        details: '/tin-dang/:id',
    },
    services: {
        category: '/dich-vu/:id/:slug',
        details: '/dich-vu/:id',
        ownerDetails: '/nha-cung-cap-dich-vu/:id',
    },
    support: {
        index: '/tro-giup',
        category: '/tro-giup/cac-phan/:id',
        article: '/tro-giup/bai-viet/:id',
        askQuestion: '/tro-giup/đặt-câu-hỏi',
        rulesAndDocuments: '/dieu-khoan',
        rulesAndDocumentsArticle: '/dieu-khoan/:id',
        privacyPolicy: '/dieu-khoan/' + SupportArticleSlug.PrivacyPolicy,
        termsOfService: '/dieu-khoan/' + SupportArticleSlug.TermsOfService,
        buySellProcess: '/dieu-khoan/' + SupportArticleSlug.BuySellProcess,
        licenseAgreement: '/dieu-khoan/' + SupportArticleSlug.RulesOfOperation,
        disputeResolution: '/dieu-khoan/' + SupportArticleSlug.DisputeResolution,
    },
}

// Текущий набор URL адресов приложения.
let currentUrls: AppUrlType = commonAppUrls
let redirects: AnyObject<string> = {}
let currentRegion: RegionCode | 'default' = 'default'

// Получить текущий набор URL адресов приложения.
export function appUrl(): AppUrlType {
    return currentUrls
}

// Установить набор URL адресов приложения для указанного региона.
export function setAppUrlsForRegion(region: RegionCode): void {
    if (currentRegion === region) {
        return
    }
    currentRegion = region
    switch (region) {
        case 'vn':
            currentUrls = vnAppUrls
            redirects = getUrlsDiff(commonAppUrls, vnAppUrls)
            break
        default:
            currentUrls = commonAppUrls
            redirects = {}
            break
    }
}

/**
 * Получить перенаправления от общих URL к региональным.
 * Возвращает только те перенаправления, которые отличаются от общих.
 */
export function getRedirectsFromCommonToRegionalUrls(): AnyObject<string> {
    return redirects
}

type NestedUrls = AnyObject<string | NestedUrls>

/**
 * Рекурсивно собирает перенаправления от общих URL к региональным.
 */
function getUrlsDiff(
    commonUrls: NestedUrls,
    regionalUrls: NestedUrls,
    redirects: AnyObject<string> = {}
): AnyObject<string> {
    if (commonUrls === regionalUrls) {
        return redirects
    }
    for (const key in commonUrls) {
        if (typeof commonUrls[key] === 'object') {
            getUrlsDiff(commonUrls[key], regionalUrls[key] as NestedUrls, redirects)
        } else if (commonUrls[key] !== regionalUrls[key]) {
            redirects[commonUrls[key]] = regionalUrls[key] as string
        }
    }
    return redirects
}
